import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  CardHeadeWithActions,
  MwButton,
  MwSpinnerButton,
  NoDataFound,
} from "../components/ui";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import AccountBookContext from "../utils/AccountsBookHelper";
import AccountContext from "../utils/AccountsHelper";
import SelectorTree from "../components/ui/SelectAccount/SelectorTree";

const ACCOUNT_BOOK_FIELDS = [
  { key: "acc_cash_account" },
  { key: "acc_banks_account" },
  { key: "acc_customer_account" },
  { key: "acc_suppliers_account" },
  { key: "acc_employee_account" },
  { key: "acc_expenses_account" },
  { key: "acc_payments_account" },
  { key: "acc_tax_receipts" },
  { key: "acc_expenses_tax" },
  { key: "acc_capital_account" },
  { key: "acc_distribution_profits_losses" },
  { key: "acc_profit_loss_account_retained" },
];

const normalizeBookData = (value) => {
  if (Array.isArray(value)) return value[0] || {};
  return value || {};
};

const toFormValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const toPayloadValue = (value) => {
  const trimmed = String(value ?? "").trim();
  return trimmed ? trimmed : null;
};

export const AccountsBook = () => {
  const { t } = useTranslation("global");
  const { accountsBook, loading, saving, saveAccountsBook } =
    useContext(AccountBookContext);
  const { accounts } = useContext(AccountContext);
  const [formValues, setFormValues] = useState({});

  const isArabic = i18next.dir(i18next.language) === "rtl";

  const bookData = useMemo(
    () => normalizeBookData(accountsBook),
    [accountsBook],
  );

  useEffect(() => {
    const initialValues = ACCOUNT_BOOK_FIELDS.reduce((acc, field) => {
      acc[field.key] = toFormValue(bookData?.[field.key]);
      return acc;
    }, {});
    setFormValues(initialValues);
  }, [bookData]);

  const accountLookup = useMemo(() => {
    const map = new Map();
    const walk = (nodes) => {
      (Array.isArray(nodes) ? nodes : []).forEach((node) => {
        const accountNumber = String(node?.account_no || "").trim();
        if (accountNumber) {
          map.set(accountNumber, node);
        }
        if (Array.isArray(node?.childs) && node.childs.length) {
          walk(node.childs);
        }
      });
    };
    walk(accounts);
    return map;
  }, [accounts]);

  const resolveLinkedAccountLabel = (accountNo) => {
    const normalized = String(accountNo || "").trim();
    if (!normalized) return "";
    const account = accountLookup.get(normalized);
    if (!account) return "";
    return isArabic
      ? account?.name_ar || account?.name_en || ""
      : account?.name_en || account?.name_ar || "";
  };

  const handleManualNumberChange = (key, number) => {
    setFormValues((prev) => ({
      ...prev,
      [key]: number,
    }));
  };

  const handleSelect = (key, account) => {
    const accountNo = account?.account_no || account?.accountNumber || "";
    setFormValues((prev) => ({
      ...prev,
      [key]: accountNo,
    }));
  };

  const handleSave = async () => {
    const payload = ACCOUNT_BOOK_FIELDS.reduce((acc, field) => {
      acc[field.key] = toPayloadValue(formValues[field.key]);
      return acc;
    }, {});
    if (bookData?.id) {
      payload.id = bookData.id;
    }
    await saveAccountsBook(payload);
  };

  return (
    <section className="p-1 font-normal">
      <CardHeadeWithActions
        title={t("menu.accounts-book")}
        icon={"/assets/icons/accounts-book-dark.png"}
      >
        <MwButton
          type="saveBtn"
          classNameCustom="!font-normal"
          onClick={handleSave}
          disabled={loading || saving}
        >
          {saving ? <MwSpinnerButton /> : t("common.save")}
        </MwButton>
      </CardHeadeWithActions>

      <div className="w-full mt-2">
        {loading ? (
          <div className="w-full h-[30vh] flex justify-center items-center">
            <MwSpinnerButton />
          </div>
        ) : ACCOUNT_BOOK_FIELDS.length === 0 ? (
          <div className="w-[50%] mx-auto mt-36 flex justify-center items-center">
            <NoDataFound msg={t("accountsBook.noData")} />
          </div>
        ) : (
          <table className="w-full table-fixed">
            <thead className="bg-[#ebf9d9]">
              <tr className="text-center text-sm text-[#261759]">
                <th className="py-1 px-2 font-normal w-[60px] whitespace-nowrap">
                  #
                </th>
                <th className="py-1 px-2 font-normal text-start">
                  {t("accountsBook.systemAccount")}
                </th>
                <th className="py-1 px-2 font-normal w-[180px] whitespace-nowrap">
                  {t("accountsBook.accountNumber")}
                </th>
                <th className="py-1 px-2 font-normal w-[260px] whitespace-nowrap">
                  {t("accountsBook.accountName")}
                </th>
              </tr>
            </thead>
            <tbody>
              {ACCOUNT_BOOK_FIELDS.map((field, index) => {
                const accountNo = formValues[field.key] || "";
                const accountLabel = resolveLinkedAccountLabel(accountNo);
                return (
                  <tr
                    key={field.key}
                    className="hover:bg-slate-200 border-b text-sm transition-all"
                  >
                    <td className="py-1 px-2 text-center text-[#261759] w-[60px] whitespace-nowrap">
                      {index + 1}
                    </td>
                    <td className="py-1 px-2 text-start text-[#261759] break-words">
                      {t(`accountsBook.fields.${field.key}`)}
                    </td>
                    <td className="py-1 px-2" colSpan={2}>
                      <SelectorTree
                        data={accounts}
                        value={accountNo}
                        label={accountLabel}
                        headerTitle=""
                        allowManualNumberInput={true}
                        withInlineSuggestions={true}
                        placeholderName={t("accountsBook.accountName")}
                        placeholderNumber={t("accountsBook.accountNumber")}
                        onManualNumberChange={(number) => {
                          handleManualNumberChange(field.key, number);
                        }}
                        onSelect={(account) => {
                          handleSelect(field.key, account);
                        }}
                        allowedAccountTypes={["secondary"]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
};
